import { useState, useEffect } from "react";
import axios from "axios";
import { track } from "../lib/track";
import { toast } from "sonner";
import {
  ArrowRight, Phone, Mail, MapPin, Menu, X, ChevronRight,
  Building2, Landmark, User, ShieldCheck, Truck, Globe2,
  PackageCheck, Stethoscope, Activity, ClipboardCheck, Lock, BadgeCheck,
  Clock, MapPinned, Layers, HeartPulse, FileCheck2, ShieldAlert,
  Send, Loader2, Facebook, Instagram, TrendingDown, Users, BarChart3,
  Boxes, Cpu, Lightbulb, Factory, Zap, Briefcase, Stethoscope as Stetho,
  Quote, CheckCircle2, ArrowUpRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_SRC = "/assets/pgps-logo.jpg";

const NAV_LINKS = [
  { id: "services", label: "Services" },
  { id: "industries", label: "Industries" },
  { id: "medical", label: "PGPS Medical" },
  { id: "about", label: "About" },
  { id: "contact", label: "Contact" },
];

const TikTokIcon = ({ size = 18 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.321 5.562a5.124 5.124 0 0 1-3.414-1.267 5.124 5.124 0 0 1-1.537-2.972h-3.131v12.388a3.087 3.087 0 1 1-2.17-2.948V7.57a6.265 6.265 0 1 0 5.436 6.196V8.48a8.265 8.265 0 0 0 4.816 1.541V6.89a5.11 5.11 0 0 1 0-1.328z" />
  </svg>
);

const SOCIAL_LINKS = [];

/* ------------------------- NAV ------------------------- */
function NavBar({ onContactClick }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-[0_2px_20px_-12px_rgba(11,31,58,.35)]" : "bg-white/85 backdrop-blur-sm"
      }`}
    >
      <div className="pg-container flex items-center justify-between h-[84px]">
        <button
          data-testid="logo-home-button"
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-3"
          aria-label="Patrick Global Home"
        >
          <div className="h-14 w-[80px] overflow-hidden flex items-center justify-center">
            <img src={LOGO_SRC} alt="Patrick Global logo" className="h-[150%] w-auto max-w-none object-contain" />
          </div>
          <div className="hidden sm:block leading-tight text-left">
            <div className="font-[Poppins] font-bold text-[17px] text-[var(--pg-navy)] tracking-tight">PATRICK GLOBAL</div>
            <div className="text-[10.5px] tracking-[0.18em] text-[var(--pg-accent)] uppercase font-semibold">Procurement &amp; Supply Chain Company</div>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}-link`}
              onClick={() => scrollTo(l.id)}
              className="font-[Poppins] font-medium text-[14px] text-[var(--pg-navy)] hover:text-[var(--pg-accent)] transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            data-testid="nav-call-link"
            href="tel:+12819019914"
            className="flex items-center gap-2 text-[14px] font-[Poppins] font-medium text-[var(--pg-navy)] hover:text-[var(--pg-accent)] transition-colors"
          >
            <Phone size={15} /> 281-901-9914
          </a>
          <button data-testid="nav-cta-button" onClick={onContactClick} className="btn-primary !py-3 !px-5 text-[14px]">
            Get a Free Consultation <ArrowRight size={15} />
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="lg:hidden p-2 rounded-md hover:bg-slate-100"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-200">
          <div className="pg-container py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}-link`}
                onClick={() => scrollTo(l.id)}
                className="text-left py-3 px-2 font-[Poppins] font-medium text-[15px] text-[var(--pg-navy)] border-b border-slate-100"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="mobile-cta-button"
              onClick={() => { setOpen(false); onContactClick(); }}
              className="btn-primary mt-3 justify-center"
            >
              Get a Free Consultation <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

/* ------------------------- HERO ------------------------- */
function Hero({ onContactClick }) {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-[140px] pb-24 md:pb-32 overflow-hidden hero-bg"
    >
      <div className="pg-container grid lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-7 fade-up">
          <span className="eyebrow" data-testid="hero-eyebrow" style={{color:"#7EC4FF"}}>Procurement &amp; Supply Chain Company</span>
          <h1 className="mt-5 text-[38px] md:text-[52px] lg:text-[60px] font-extrabold leading-[1.04] text-white">
            A Procurement &amp; Supply Chain Company That
            <span className="relative inline-block px-1 mx-1">
              <span className="relative z-10 text-[var(--pg-accent)]">Reduces Cost</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-[var(--pg-accent)]/25 -z-0 rounded-sm"></span>
            </span>
            and Drives Growth.
          </h1>
          <p className="mt-6 text-[17px] md:text-[19px] text-white/80 max-w-2xl leading-relaxed">
            We help businesses optimize procurement, streamline supply chains, and
            improve operational efficiency through data-driven strategies and
            global sourcing expertise.
          </p>

          <div className="mt-9 flex flex-wrap gap-3" data-testid="hero-cta-row">
            <button data-testid="hero-primary-cta" onClick={onContactClick} className="btn-primary">
              Get a Free Consultation <ArrowRight size={18} />
            </button>
            <button
              data-testid="hero-quote-cta"
              onClick={onContactClick}
              className="btn-orange-accent"
            >
              Submit an RFQ <ArrowRight size={18} />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-ghost-on-dark"
            >
              Explore Our Services <ChevronRight size={18} />
            </button>
          </div>

          <p className="mt-8 text-[13.5px] text-white/65 max-w-xl" data-testid="hero-trust-line">
            For organizations seeking smarter procurement and scalable supply chain solutions.
          </p>
        </div>

        <div className="lg:col-span-5 fade-up delay-2">
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-[var(--pg-accent)]/15 blur-2xl"></div>
            <div className="absolute -bottom-10 -right-6 w-40 h-40 rounded-full bg-[var(--pg-navy)]/20 blur-2xl"></div>
            <div className="relative bg-white border border-slate-200 rounded-2xl shadow-[0_24px_60px_-30px_rgba(11,31,58,.45)] p-6 md:p-7">
              <img
                src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1400&q=75"
                alt="Modern logistics warehouse with organized inventory and pallets — procurement and supply chain operations"
                loading="eager"
                className="w-full h-56 md:h-64 object-cover rounded-xl"
              />
              <div className="mt-5 grid grid-cols-3 gap-3">
                <StatTile value="End-to-End" label="Sourcing to delivery" />
                <StatTile value="Global" label="Sourcing reach" />
                <StatTile value="KPI-Driven" label="Reporting" />
              </div>
              <div className="mt-5 flex items-center gap-3 p-4 rounded-xl bg-[var(--pg-bg)]">
                <div className="w-10 h-10 rounded-full bg-[var(--pg-accent)] flex items-center justify-center text-white">
                  <BarChart3 size={20} />
                </div>
                <div>
                  <div className="font-[Poppins] font-semibold text-[14px]">Data-driven strategy</div>
                  <div className="text-[12.5px] text-[var(--pg-ink-soft)]">Assess → optimize → scale with measurable KPIs.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatTile({ value, label }) {
  return (
    <div className="text-center bg-[var(--pg-bg)] rounded-lg py-3 px-2">
      <div className="font-[Poppins] font-bold text-[15px] text-[var(--pg-navy)]">{value}</div>
      <div className="text-[10.5px] uppercase tracking-wider text-[var(--pg-ink-soft)] mt-1 font-semibold">{label}</div>
    </div>
  );
}

/* ---------------------- TRUST STRIP --------------------- */
export function TrustStrip() {
  const tags = ["Government", "Healthcare", "Energy", "Manufacturing", "Technology", "SMBs"];
  return (
    <section data-testid="trust-strip" className="border-y border-slate-200 bg-[var(--pg-bg)] py-6">
      <div className="pg-container flex flex-col md:flex-row items-center justify-between gap-5">
        <p className="font-[Poppins] font-semibold text-[13px] tracking-[0.18em] uppercase text-[var(--pg-ink-soft)]">
          Serving businesses and organizations across multiple industries
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          {tags.map((t) => (
            <span key={t} className="font-[Poppins] font-semibold text-[13px] text-[var(--pg-navy)] flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--pg-accent)]"></span>{t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------ SERVICES ---------------------- */
export function Services() {
  const services = [
    { icon: PackageCheck, title: "Procurement & Strategic Sourcing", body: "Identify cost-saving opportunities and build strategic supplier relationships." },
    { icon: Boxes,        title: "Supply Chain Optimization",        body: "Improve efficiency, reduce delays, and enhance operational performance." },
    { icon: Users,        title: "Vendor Management",                body: "Streamline supplier relationships and improve vendor accountability." },
    { icon: Truck,        title: "Logistics & Distribution",         body: "Optimize transportation, warehousing, and delivery systems." },
    { icon: Cpu,          title: "IT & Systems Integration",         body: "Implement technology solutions to enhance procurement and supply chain visibility." },
    { icon: Lightbulb,    title: "Consulting & Advisory",            body: "Expert guidance to align procurement strategy with business goals." },
  ];
  return (
    <section id="services" data-testid="services-section" className="py-20 md:py-28 bg-white">
      <div className="pg-container">
        <div className="max-w-3xl">
          <span className="eyebrow">What We Do</span>
          <h2 className="mt-5 text-[34px] md:text-[44px] font-extrabold leading-[1.08]">
            Procurement &amp; Supply Chain Services
          </h2>
          <p className="mt-5 text-[16.5px] text-[var(--pg-ink-soft)] leading-relaxed">
            Our procurement consulting services and supply chain solutions are
            designed to help organizations <strong className="text-[var(--pg-navy)]">reduce costs</strong>,
            <strong className="text-[var(--pg-navy)]"> improve efficiency</strong>, and build
            resilient operations.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => (
            <div
              key={s.title}
              data-testid={`service-card-${i}`}
              className="lift-card group bg-white border border-slate-200 rounded-2xl p-7"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white" style={{background: "linear-gradient(135deg, #0B1F3A 0%, #1E90FF 100%)"}}>
                <s.icon size={22} />
              </div>
              <h3 className="mt-5 font-[Poppins] font-bold text-[19px]">{s.title}</h3>
              <p className="mt-2 text-[14.5px] text-[var(--pg-ink-soft)] leading-relaxed">{s.body}</p>
              <div className="mt-5 inline-flex items-center gap-2 text-[13px] font-[Poppins] font-semibold text-[var(--pg-accent)] group-hover:gap-3 transition-all">
                Learn more <ArrowRight size={14} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------- VALUE PROP ---------------------- */
export function ValueProp() {
  const bullets = [
    "Reduce procurement costs and increase savings",
    "Improve supplier performance and reliability",
    "Enhance supply chain visibility and control",
    "Scale operations with confidence",
  ];
  const metrics = [
    { value: "Lower Costs", label: "Sourcing & Negotiation Strategy" },
    { value: "Visibility", label: "Supply Chain Control" },
    { value: "Global", label: "Supplier Network Access" },
  ];
  return (
    <section data-testid="value-prop-section" className="py-20 md:py-28 bg-[var(--pg-bg)]">
      <div className="pg-container grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-6">
          <span className="eyebrow">Value Proposition</span>
          <h2 className="mt-5 text-[34px] md:text-[42px] font-extrabold leading-[1.08]">
            Why Businesses Choose Patrick Global
          </h2>
          <p className="mt-5 text-[16.5px] text-[var(--pg-ink-soft)] leading-relaxed">
            We help organizations source products, equipment, and supplies by identifying suppliers,
            comparing competitive pricing, coordinating purchasing, and managing delivery.
          </p>
          <ul className="mt-8 space-y-3" data-testid="value-prop-bullets">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-3 text-[15.5px] text-[var(--pg-navy)] font-medium">
                <CheckCircle2 size={22} className="text-[var(--pg-accent)] mt-0.5 flex-shrink-0" />
                {b}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-6">
          <div className="grid grid-cols-1 gap-4" data-testid="value-prop-metrics">
            {metrics.map((m, i) => (
              <div
                key={m.label}
                data-testid={`metric-${i}`}
                className="bg-white border border-slate-200 rounded-2xl p-6 md:p-7 flex items-center justify-between gap-6 lift-card"
              >
                <div>
                  <div className="font-[Poppins] font-extrabold text-[28px] md:text-[34px] text-[var(--pg-navy)] leading-none">{m.value}</div>
                  <div className="mt-2 font-[Poppins] font-semibold text-[13.5px] tracking-[0.12em] uppercase text-[var(--pg-ink-soft)]">{m.label}</div>
                </div>
                <div className="w-12 h-12 rounded-xl bg-[var(--pg-accent)]/10 text-[var(--pg-accent)] flex items-center justify-center flex-shrink-0">
                  {i === 0 ? <TrendingDown size={24} /> : i === 1 ? <Zap size={24} /> : <Globe2 size={24} />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------- WHO WE SERVE --------------------- */
export function WhoWeServe() {
  const groups = [
    {
      icon: Landmark,
      title: "Government",
      detail: "Federal · State · Municipal · Government Contractors",
      image: "https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?auto=format&fit=crop&w=1200&q=70",
      alt: "U.S. Capitol building representing government clients",
      testid: "audience-government",
    },
    {
      icon: Building2,
      title: "Industrial & Commercial",
      detail: "Energy · Oil & Gas · Manufacturing · Construction · Engineering",
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=70",
      alt: "Corporate office skyscrapers representing industrial and commercial clients",
      testid: "audience-industrial",
    },
    {
      icon: HeartPulse,
      title: "Healthcare",
      detail: "Hospitals · Laboratories · Medical Distributors",
      image: "https://images.unsplash.com/photo-1586773860418-d37222d8fce3?auto=format&fit=crop&w=1200&q=70",
      alt: "Modern hospital building representing healthcare clients",
      testid: "audience-healthcare",
    },
    {
      icon: Briefcase,
      title: "Growing Businesses",
      detail: "Small & Mid-Sized Businesses",
      image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1200&q=70",
      alt: "Team collaborating in a small business office",
      testid: "audience-growing",
    },
  ];
  return (
    <section id="who-we-serve" data-testid="who-we-serve-section" className="py-20 md:py-24 bg-white">
      <div className="pg-container">
        <div className="max-w-2xl">
          <span className="eyebrow">Who We Serve</span>
          <h2 className="mt-5 text-[34px] md:text-[42px] font-extrabold leading-[1.08]">
            Four client categories. One uncompromising standard.
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {groups.map((g) => (
            <div
              key={g.title}
              data-testid={g.testid}
              className="bg-white border border-slate-200 rounded-2xl overflow-hidden lift-card group"
            >
              <div className="relative h-40 md:h-44 overflow-hidden">
                <img src={g.image} alt={g.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[rgba(11,31,58,0.75)] via-[rgba(11,31,58,0.15)] to-transparent"></div>
                <div className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-white text-[var(--pg-accent)] flex items-center justify-center shadow-md">
                  <g.icon size={22} />
                </div>
              </div>
              <div className="p-6 md:p-7">
                <h3 className="font-[Poppins] font-bold text-[20px] text-[var(--pg-navy)]">{g.title}</h3>
                <p className="mt-2 text-[14.5px] text-[var(--pg-ink-soft)] leading-relaxed">{g.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------- INDUSTRIES ---------------------- */
export function Industries() {
  const items = [
    { icon: Landmark,    title: "Government",          body: "Federal, state, municipal, and international procurement programs." },
    { icon: Stetho,      title: "Healthcare",          body: "Hospitals, labs, pharmacies — supported by PGPS Medical Services." },
    { icon: Zap,         title: "Energy",              body: "Oil, gas, renewables — upstream to downstream sourcing." },
    { icon: Factory,     title: "Manufacturing",       body: "Direct and indirect materials, MRO, and contract manufacturing." },
    { icon: Cpu,         title: "Technology",          body: "Hardware, software, and IT services procurement & vendor strategy." },
    { icon: Briefcase,   title: "Small & Medium Businesses", body: "Scalable procurement programs tailored to growing organizations." },
  ];
  return (
    <section id="industries" data-testid="industries-section" className="py-20 md:py-28 bg-[var(--pg-bg)]">
      <div className="pg-container">
        <div className="max-w-2xl">
          <span className="eyebrow">Sector Expertise</span>
          <h2 className="mt-5 text-[34px] md:text-[44px] font-extrabold leading-[1.08]">
            Industries We Serve
          </h2>
          <p className="mt-5 text-[16.5px] text-[var(--pg-ink-soft)] leading-relaxed">
            We provide tailored procurement and supply chain company solutions across a wide range of industries.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it, i) => (
            <div
              key={it.title}
              data-testid={`industry-${i}`}
              className="lift-card bg-white border border-slate-200 rounded-2xl p-7 flex flex-col"
            >
              <div className="w-12 h-12 rounded-xl bg-[var(--pg-navy)]/5 text-[var(--pg-navy)] flex items-center justify-center">
                <it.icon size={22} />
              </div>
              <h3 className="mt-5 font-[Poppins] font-bold text-[19px]">{it.title}</h3>
              <p className="mt-2 text-[14.5px] text-[var(--pg-ink-soft)] leading-relaxed flex-1">{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------- OPERATIONS ---------------------- */
export function OperationsGallery() {
  const tiles = [
    { src: "https://images.unsplash.com/photo-1569154941061-e231b4725ef1?auto=format&fit=crop&w=1400&q=75", alt: "Cargo airplane being loaded at airport — global air freight operations",  label: "Global Freight",       caption: "Air freight, cross-border shipping & customs." },
    { src: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=1200&q=70", alt: "Logistics truck fleet on highway",                            label: "Domestic Logistics",   caption: "Fleet-grade freight & last-mile." },
    { src: "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1200&q=70", alt: "Warehouse pallets with forklift",                              label: "Warehousing",          caption: "Inventory, staging & distribution." },
    { src: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1200&q=70", alt: "Procurement team planning session",                           label: "Strategic Sourcing",   caption: "Supplier vetting & contract planning." },
  ];
  return (
    <section data-testid="operations-section" className="py-20 md:py-24 bg-white">
      <div className="pg-container">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <span className="eyebrow">Operations in Motion</span>
            <h2 className="mt-5 text-[32px] md:text-[40px] font-extrabold leading-[1.08]">
              From origin port to loading dock — we optimize it.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[16px] text-[var(--pg-ink-soft)] leading-relaxed">
              Procurement is only half the story. Our consulting and operations teams
              coordinate freight, warehousing, customs, and last-mile delivery so every
              engagement lands intact, on time, and documented.
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-12 gap-4 md:gap-5">
          <GalleryTile tile={tiles[0]} className="md:col-span-7 md:row-span-2 aspect-[16/10] md:aspect-auto md:min-h-[440px]" testid="gallery-tile-0" />
          <GalleryTile tile={tiles[1]} className="md:col-span-5 aspect-[4/3] md:aspect-[16/9]" testid="gallery-tile-1" />
          <GalleryTile tile={tiles[2]} className="md:col-span-5 aspect-[4/3] md:aspect-[16/9]" testid="gallery-tile-2" />
          <GalleryTile tile={tiles[3]} className="md:col-span-12 aspect-[16/6]" testid="gallery-tile-3" />
        </div>
      </div>
    </section>
  );
}

function GalleryTile({ tile, className = "", testid }) {
  return (
    <div data-testid={testid} className={`relative overflow-hidden rounded-2xl group ${className}`}>
      <img src={tile.src} alt={tile.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(11,31,58,0.85)] via-[rgba(11,31,58,0.15)] to-transparent"></div>
      <div className="relative h-full flex flex-col justify-end p-5 md:p-7">
        <div className="inline-flex w-fit items-center gap-2 text-[10.5px] font-[Poppins] font-semibold tracking-[0.22em] uppercase bg-[var(--pg-accent)] text-white px-3 py-1.5 rounded-full">
          {tile.label}
        </div>
        <div className="mt-3 text-white font-[Poppins] font-semibold text-[16px] md:text-[18px] max-w-md">{tile.caption}</div>
      </div>
    </div>
  );
}

/* ------------------------- ABOUT ------------------------ */
export function About() {
  return (
    <section id="about" data-testid="about-section" className="py-20 md:py-28 bg-white">
      <div className="pg-container">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <span className="eyebrow">About Patrick Global</span>
            <h2 className="mt-5 text-[34px] md:text-[44px] font-extrabold leading-[1.08]">
              A Procurement Partner Built Around Your Requirements
            </h2>
            <p className="mt-6 text-[16.5px] text-[var(--pg-ink-soft)] leading-relaxed">
              Patrick Global helps organizations source
              <strong className="text-[var(--pg-navy)]"> products</strong>,
              <strong className="text-[var(--pg-navy)]"> equipment</strong>,
              <strong className="text-[var(--pg-navy)]"> supplies</strong>, and procurement requirements
              through structured sourcing, supplier coordination, procurement management, and logistics support.
            </p>
            <p className="mt-4 text-[16.5px] text-[var(--pg-ink-soft)] leading-relaxed">
              From formal RFQs to simple purchase lists, we manage supplier outreach, quote comparison,
              purchasing coordination, and delivery logistics — with consulting and advisory available as a
              supporting service. Operating from our Houston, Texas headquarters, we serve organizations
              domestically and internationally.
            </p>
            <div className="mt-6 text-[13.5px] text-[var(--pg-ink-soft)]">
              <span className="font-[Poppins] font-semibold text-[var(--pg-navy)]">Patrick Global Procurement Services</span> · Houston, TX
            </div>
          </div>

          <div className="lg:col-span-6 grid sm:grid-cols-2 gap-4">
            {[
              { icon: ShieldCheck,     title: "Compliance-first", body: "Operating within applicable regulatory frameworks for every engagement." },
              { icon: Globe2,          title: "Global reach",     body: "From local sourcing to cross-border supplier management." },
              { icon: PackageCheck,    title: "Vetted network",   body: "Curated supplier base screened for quality, capacity & cost." },
              { icon: ClipboardCheck,  title: "Clear reporting",  body: "Documented savings, KPIs, and performance transparency." },
            ].map((c) => (
              <div key={c.title} className="lift-card border border-slate-200 rounded-2xl p-6 bg-white">
                <div className="w-11 h-11 rounded-xl text-[var(--pg-accent)] flex items-center justify-center" style={{background: "rgba(30,144,255,.10)"}}>
                  <c.icon size={22} />
                </div>
                <h3 className="mt-4 font-[Poppins] font-bold text-[18px]">{c.title}</h3>
                <p className="mt-2 text-[14.5px] text-[var(--pg-ink-soft)] leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 md:mt-20">
          <div
            data-testid="why-partner-panel"
            className="rounded-2xl overflow-hidden border border-slate-200 bg-gradient-to-r from-[var(--pg-navy)] to-[var(--pg-navy-2)] text-white p-8 md:p-12"
          >
            <span className="text-[11px] font-[Poppins] font-semibold tracking-[0.22em] uppercase text-[var(--pg-accent)]">Why Partner With Patrick Global?</span>
            <h3 className="mt-3 font-[Poppins] font-extrabold text-[26px] md:text-[32px] leading-[1.12] !text-white">
              One Procurement Partner. From Requirement to Delivery.
            </h3>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: PackageCheck, title: "Source Smarter", body: "We help identify qualified suppliers for the products and equipment your organization needs." },
                { icon: BarChart3, title: "Compare Options", body: "Evaluate pricing, availability, lead times, logistics, and other requirements before making a purchasing decision." },
                { icon: ClipboardCheck, title: "Simplify Procurement", body: "Work with one procurement partner instead of managing multiple sourcing conversations yourself." },
                { icon: Truck, title: "Coordinate Delivery", body: "We help coordinate the movement of products from supplier to destination." },
              ].map((c) => (
                <div key={c.title} data-testid={`why-partner-${c.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="w-11 h-11 rounded-xl bg-white/10 text-[var(--pg-accent)] flex items-center justify-center">
                    <c.icon size={22} />
                  </div>
                  <h4 className="mt-4 font-[Poppins] font-bold text-[17px] !text-white">{c.title}</h4>
                  <p className="mt-2 text-[14px] text-white/75 leading-relaxed">{c.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-col md:flex-row md:items-center gap-5 justify-between border-t border-white/15 pt-8">
              <p className="text-[15px] text-white/85 max-w-2xl">
                <span className="font-[Poppins] font-semibold !text-white">Built Around Your Requirement</span> — whether you have a formal RFQ,
                a purchase list, a specification, or simply know what you need, we can start there.
              </p>
              <a href="/request-a-quote" data-testid="why-partner-cta"
                className="btn-primary !py-3.5 !px-6 text-[13px] whitespace-nowrap w-fit">
                SUBMIT AN RFQ <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------- WHAT CLIENTS CAN EXPECT ------------------ */
export function ClientExpectations() {
  const items = [
    "Responsive communication",
    "Structured sourcing",
    "Competitive supplier options",
    "Clear procurement information",
    "Supplier coordination",
    "Delivery coordination",
  ];
  return (
    <section id="client-expectations" data-testid="client-expectations-section" className="py-20 md:py-24 bg-[var(--pg-bg,#F6F8FB)]">
      <div className="pg-container">
        <div className="max-w-2xl">
          <span className="eyebrow">Working With Us</span>
          <h2 className="mt-5 text-[34px] md:text-[42px] font-extrabold leading-[1.08]">
            What Clients Can Expect
          </h2>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => (
            <div key={t} data-testid={`expectation-${t.toLowerCase().replace(/\s+/g, "-")}`}
              className="lift-card bg-white border border-slate-200 rounded-2xl p-6 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl text-[var(--pg-accent)] flex items-center justify-center flex-shrink-0" style={{ background: "rgba(30,144,255,.10)" }}>
                <CheckCircle2 size={20} />
              </div>
              <span className="font-[Poppins] font-semibold text-[15.5px] text-[var(--pg-navy)]">{t}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- MEDICAL SUBSIDIARY -------------------- */
export function MedicalSubsidiary({ onContactClick }) {
  const why = [
    { icon: ShieldCheck, title: "Enterprise-Level Reliability",    body: "Structured dispatch systems and disciplined operations ensure on-time, every-time delivery." },
    { icon: BadgeCheck,  title: "Trained & Professional Personnel", body: "Drivers and handlers trained in medical logistics protocols and confidentiality standards." },
    { icon: Activity,    title: "Real-Time Visibility",              body: "Tracking and communication systems that keep clients informed at every stage of delivery." },
    { icon: Layers,      title: "Scalable Operations",               body: "Capacity to support both routine deliveries and high-volume healthcare logistics demands." },
    { icon: HeartPulse,  title: "Client-Centric Execution",          body: "Flexible service models tailored to hospitals, labs, and pharmacies." },
  ];
  const compliance = [
    { icon: Lock,        title: "HIPAA-Aligned Processes",          body: "Protection of patient information and confidentiality at every touchpoint." },
    { icon: ShieldAlert, title: "OSHA Awareness & Safety Standards", body: "Safe handling of medical materials and workplace compliance." },
    { icon: FileCheck2,  title: "Background-Checked Personnel",      body: "Ensuring trust, professionalism, and accountability." },
    { icon: ClipboardCheck, title: "Secure Handling Protocols",      body: "Chain-of-custody procedures for sensitive and time-critical deliveries." },
    { icon: ShieldCheck,  title: "Fully Insured Operations",         body: "Coverage designed to protect clients and their assets." },
  ];
  return (
    <section id="medical" data-testid="medical-section" className="dark-section py-24 md:py-32">
      <div className="pg-container">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-[11px] font-[Poppins] font-semibold tracking-[0.22em] uppercase text-[var(--pg-orange)]">
              <Stethoscope size={14} /> PGPS Medical Services · A Patrick Global Subsidiary
            </span>
            <h2 className="mt-5 text-white font-[Poppins] font-extrabold text-[36px] md:text-[52px] leading-[1.04]">
              PGPS Medical Services
            </h2>
            <p className="mt-5 text-white/80 text-[16.5px] md:text-[17.5px] leading-relaxed max-w-2xl">
              The healthcare division of Patrick Global Procurement Services,
              established to deliver specialized medical courier, logistics,
              procurement, and supply chain solutions with the precision,
              discipline, and operational excellence and speed that define our brand.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden border border-white/15 bg-white/5 backdrop-blur-sm">
              <div className="grid grid-cols-3 gap-0.5" data-testid="medical-facility-images">
                {[
                  { src: "https://images.unsplash.com/photo-1586773860418-d37222d8fce3?auto=format&fit=crop&w=800&q=70", alt: "Hospital corridor", label: "Hospital" },
                  { src: "https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&w=800&q=70", alt: "Laboratory technician",  label: "Laboratory" },
                  { src: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&w=800&q=70", alt: "Pharmacy shelves",        label: "Pharmacy" },
                ].map((f) => (
                  <figure key={f.label} className="relative h-[140px] md:h-[160px] overflow-hidden">
                    <img src={f.src} alt={f.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[rgba(11,31,58,0.85)] via-transparent to-transparent"></div>
                    <figcaption className="absolute bottom-2 left-2 right-2 text-white text-[10.5px] font-[Poppins] font-semibold tracking-[0.18em] uppercase">{f.label}</figcaption>
                  </figure>
                ))}
              </div>
              <div className="p-6">
                <p className="text-white/85 text-[15px] leading-relaxed">
                  We support hospitals, laboratories, pharmacies, and medical distributors —
                  with mission-critical services that demand <span className="text-[var(--pg-orange)] font-semibold">speed, accuracy, and strict compliance</span>.
                </p>
                <p className="mt-4 text-white/85 text-[15px] leading-relaxed">
                  Every delivery directly impacts patient outcomes.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose */}
        <div className="mt-20">
          <h3 className="text-white font-[Poppins] font-bold text-[24px] md:text-[28px]">Why Choose PGPS Medical Services</h3>
          <p className="mt-3 text-white/70 max-w-2xl text-[15.5px]">A reliable logistics partner — not just a courier service.</p>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {why.map((w, i) => (
              <div key={w.title} data-testid={`medical-why-${i}`} className="bg-white/[0.04] border border-white/10 hover:border-[var(--pg-orange)]/60 rounded-2xl p-6 transition-colors">
                <div className="w-11 h-11 rounded-lg bg-[var(--pg-orange)] text-white flex items-center justify-center">
                  <w.icon size={20} />
                </div>
                <h4 className="mt-4 text-white font-[Poppins] font-semibold text-[17px]">{w.title}</h4>
                <p className="mt-2 text-white/75 text-[14px] leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance */}
        <div className="mt-20">
          <h3 className="text-white font-[Poppins] font-bold text-[24px] md:text-[28px]">Certifications &amp; Compliance</h3>
          <p className="mt-3 text-white/70 max-w-2xl text-[15.5px]">Healthcare logistics requires strict adherence to regulatory and safety standards.</p>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {compliance.map((c, i) => (
              <div key={c.title} data-testid={`medical-compliance-${i}`} className="bg-white/[0.04] border border-white/10 rounded-2xl p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[var(--pg-accent)]/15 text-[var(--pg-accent)] flex items-center justify-center">
                    <c.icon size={20} />
                  </div>
                  <h4 className="text-white font-[Poppins] font-semibold text-[16px]">{c.title}</h4>
                </div>
                <p className="mt-3 text-white/75 text-[14px] leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 flex items-center gap-3 text-white/80 text-[14.5px]">
            <MapPinned size={18} className="text-[var(--pg-orange)]" />
            <span>Localized presence enables rapid response, efficient routing, and dependable last-mile delivery.</span>
          </div>
        </div>

        {/* Commitment CTA */}
        <div className="mt-20 bg-gradient-to-r from-[var(--pg-orange)] to-[#F59E3D] rounded-3xl p-8 md:p-12 relative overflow-hidden">
          <div className="absolute -right-20 -bottom-20 w-72 h-72 rounded-full bg-white/10"></div>
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <h3 className="text-white font-[Poppins] font-extrabold text-[28px] md:text-[34px] leading-tight">Our Commitment</h3>
              <p className="mt-4 text-white/95 text-[16.5px] max-w-2xl leading-relaxed">
                Backed by the leadership and infrastructure of Patrick Global, our commitment is clear:
                to deliver compliant, efficient, and high-performance medical logistics solutions that
                healthcare providers can depend on, without compromise.
              </p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <button data-testid="medical-cta-button" onClick={onContactClick} className="inline-flex items-center gap-2 bg-white text-[var(--pg-navy)] font-[Poppins] font-semibold px-6 py-4 rounded-full hover:bg-[var(--pg-navy)] hover:text-white transition-colors">
                Talk to PGPS Medical <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------ CONTACT ----------------------- */
const SERVICE_OPTIONS = [
  "General Inquiry",
  "Procurement & Strategic Sourcing",
  "Supply Chain Optimization",
  "Vendor Management",
  "Logistics & Distribution",
  "IT & Systems Integration",
  "Consulting & Advisory",
  "PGPS Medical Services",
];

export function ContactSection() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    service_interest: "General Inquiry", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handle = (k) => (e) => { track("inquiry_started", {}, true); setForm((f) => ({ ...f, [k]: e.target.value })); };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error("Please complete name, email and a brief message.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      track("inquiry_submitted", { reference: data.reference, source: "home" });
      toast.success(data.reference ? `${data.message} Reference: ${data.reference}` : (data.message || "Message received."));
      setForm({ name: "", email: "", phone: "", company: "", service_interest: "General Inquiry", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again or call 281-901-9914.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-28 bg-white">
      <div className="pg-container">
        {/* Final CTA banner */}
        <div
          data-testid="final-cta-banner"
          className="rounded-3xl bg-gradient-to-r from-[var(--pg-navy)] to-[var(--pg-navy-2)] text-white p-8 md:p-12 mb-14 md:mb-16 relative overflow-hidden"
        >
          <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full bg-[var(--pg-accent)]/20 blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-16 -left-16 w-80 h-80 rounded-full bg-[var(--pg-orange)]/15 blur-3xl pointer-events-none"></div>
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <span className="inline-flex items-center gap-2 text-[11px] font-[Poppins] font-semibold tracking-[0.22em] uppercase text-[var(--pg-accent)]">
                <ArrowUpRight size={14} /> Next Step
              </span>
              <h2 className="mt-4 text-white font-[Poppins] font-extrabold text-[30px] md:text-[42px] leading-[1.08]">
                Ready to Optimize Your Procurement and Supply Chain Strategy?
              </h2>
              <p className="mt-4 text-white/85 text-[16.5px] max-w-2xl">
                Partner with experts who deliver measurable results and long-term value.
              </p>
            </div>
            <div className="md:col-span-4 flex md:justify-end flex-wrap gap-3">
              <a
                href="/request-a-quote"
                data-testid="final-cta-button"
                className="inline-flex items-center gap-2 bg-[var(--pg-accent)] hover:bg-white hover:text-[var(--pg-navy)] text-white font-[Poppins] font-semibold px-6 py-4 rounded-full transition-colors"
              >
                Submit an RFQ <ArrowRight size={18} />
              </a>
              <a
                href="#contact-form"
                data-testid="final-cta-quote-button"
                className="inline-flex items-center gap-2 bg-transparent hover:bg-white hover:text-[var(--pg-navy)] text-white font-[Poppins] font-semibold px-6 py-4 rounded-full border border-white/40 transition-colors"
              >
                Talk to a Procurement Specialist <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="eyebrow">Get in Touch</span>
            <h2 className="mt-5 text-[34px] md:text-[44px] font-extrabold leading-[1.08]">
              Let’s build a smarter supply chain.
            </h2>
            <p className="mt-5 text-[16.5px] text-[var(--pg-ink-soft)] leading-relaxed">
              Tell us about your procurement or supply chain need. A Patrick Global specialist will respond within one business day.
            </p>
            <div className="mt-8 space-y-4">
              <ContactRow icon={MapPin} title="Headquarters" body="8700 Commerce Park Drive, Suite 238, Houston, Texas 77036" />
              <ContactRow icon={Phone}  title="Call us"      body={<a href="tel:+12819019914" data-testid="home-contact-phone" onClick={() => track("phone_click", { source: "home_contact" })} className="hover:text-[var(--pg-accent)]">281-901-9914</a>} />
              <ContactRow icon={Mail}   title="Email"        body={<a href="mailto:info@pgpsusa.com" data-testid="home-contact-email" onClick={() => track("email_click", { source: "home_contact" })} className="hover:text-[var(--pg-accent)]">info@pgpsusa.com</a>} />
              <ContactRow icon={Clock}  title="Operations"   body="Support for active engagements" />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              id="contact-form"
              data-testid="contact-form"
              onSubmit={submit}
              className="bg-[var(--pg-bg)] border border-slate-200 rounded-2xl p-6 md:p-8"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="pg-label" htmlFor="contact-name">Full Name *</label>
                  <input id="contact-name" data-testid="contact-name-input" className="pg-input" value={form.name} onChange={handle("name")} placeholder="Jane Doe" required />
                </div>
                <div>
                  <label className="pg-label" htmlFor="contact-email">Email *</label>
                  <input id="contact-email" data-testid="contact-email-input" type="email" className="pg-input" value={form.email} onChange={handle("email")} placeholder="jane@company.com" required />
                </div>
                <div>
                  <label className="pg-label" htmlFor="contact-phone">Phone</label>
                  <input id="contact-phone" data-testid="contact-phone-input" className="pg-input" value={form.phone} onChange={handle("phone")} placeholder="+1 ___ ___ ____" />
                </div>
                <div>
                  <label className="pg-label" htmlFor="contact-company">Company / Organization</label>
                  <input id="contact-company" data-testid="contact-company-input" className="pg-input" value={form.company} onChange={handle("company")} placeholder="Acme Corp" />
                </div>
                <div className="sm:col-span-2">
                  <label className="pg-label" htmlFor="contact-service">Service Interest</label>
                  <select id="contact-service" data-testid="contact-service-select" className="pg-input" value={form.service_interest} onChange={handle("service_interest")}>
                    {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="pg-label" htmlFor="contact-message">How can we help? *</label>
                  <textarea id="contact-message" data-testid="contact-message-input" rows={5} className="pg-input resize-y" value={form.message} onChange={handle("message")} placeholder="Briefly describe your procurement or supply chain requirement..." required />
                </div>
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                <p className="text-[12.5px] text-[var(--pg-ink-soft)] max-w-md">
                  By submitting, you agree to be contacted by Patrick Global regarding your inquiry. We do not share contact details.
                </p>
                <button data-testid="contact-submit-button" type="submit" disabled={submitting} className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed">
                  {submitting ? <><Loader2 className="animate-spin" size={18} /> Sending...</> : <>Send Message <Send size={16} /></>}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Closing tagline */}
        <div className="mt-16 md:mt-20">
          <div
            data-testid="contact-tagline"
            className="relative rounded-2xl border border-slate-200 bg-gradient-to-r from-[var(--pg-bg)] via-white to-[var(--pg-bg)] py-10 md:py-12 px-6 md:px-10 overflow-hidden"
          >
            <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-[var(--pg-orange)]/10 blur-3xl pointer-events-none"></div>
            <div className="absolute -left-10 -bottom-10 w-48 h-48 rounded-full bg-[var(--pg-accent)]/10 blur-3xl pointer-events-none"></div>
            <blockquote className="relative text-center max-w-4xl mx-auto">
              <Quote size={28} className="mx-auto mb-4 text-[var(--pg-accent)]" />
              <p className="font-[Poppins] font-semibold text-[20px] md:text-[26px] leading-[1.35] text-[var(--pg-navy)]">
                Built on <span className="text-[var(--pg-accent)]">trust</span>,
                <span className="text-[var(--pg-accent)]"> innovation</span>, and
                <span className="text-[var(--pg-accent)]"> results</span> — we are shaping
                the future of procurement and supply chain worldwide.
              </p>
            </blockquote>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-[var(--pg-accent)] flex-shrink-0">
        <Icon size={18} />
      </div>
      <div>
        <div className="font-[Poppins] font-semibold text-[14px] text-[var(--pg-navy)]">{title}</div>
        <div className="text-[14.5px] text-[var(--pg-ink-soft)] mt-0.5">{body}</div>
      </div>
    </div>
  );
}

/* ------------------------ FOOTER ------------------------ */
function Footer() {
  return (
    <footer data-testid="site-footer" className="dark-section pt-16 pb-8 mt-0">
      <div className="pg-container">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="bg-white rounded-xl p-3 w-fit overflow-hidden">
              <div className="h-20 w-[120px] overflow-hidden flex items-center justify-center">
                <img src={LOGO_SRC} alt="Patrick Global" className="h-[150%] w-auto max-w-none object-contain" />
              </div>
            </div>
            <p className="mt-5 text-white/75 text-[15px] leading-relaxed max-w-md">
              <strong className="text-white">Patrick Global</strong> provides procurement consulting,
              supply chain optimization, and logistics consulting services across the United States and Internationally.
            </p>
            <p className="mt-3 text-white/60 text-[13px] leading-relaxed">
              Patrick Global Procurement Services — a Procurement and Supply Chain Company serving government, industrial & commercial, healthcare, and growing businesses.
            </p>
            <div className="mt-6 flex items-center gap-3" data-testid="social-links">
              {SOCIAL_LINKS.map(({ label, href, Icon, testid }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={testid}
                  className="w-10 h-10 rounded-full border border-white/20 bg-white/5 text-white/85 hover:text-white hover:bg-[var(--pg-accent)] hover:border-[var(--pg-accent)] flex items-center justify-center transition-colors"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>
          <div className="md:col-span-3">
            <h4 className="text-white font-[Poppins] font-bold text-[15px] tracking-wider uppercase">Company</h4>
            <ul className="mt-4 space-y-2 text-[14.5px]">
              <li><a href="#services" className="text-white/75 hover:text-[var(--pg-accent)] transition-colors">Services</a></li>
              <li><a href="#industries" className="text-white/75 hover:text-[var(--pg-accent)] transition-colors">Industries</a></li>
              <li><a href="#about" className="text-white/75 hover:text-[var(--pg-accent)] transition-colors">About</a></li>
              <li><a href="#medical" className="text-white/75 hover:text-[var(--pg-accent)] transition-colors">PGPS Medical Services</a></li>
              <li><a href="#contact" className="text-white/75 hover:text-[var(--pg-accent)] transition-colors">Contact</a></li>
            </ul>
          </div>
          <div className="md:col-span-4">
            <h4 className="text-white font-[Poppins] font-bold text-[15px] tracking-wider uppercase">Contact</h4>
            <ul className="mt-4 space-y-3 text-[14.5px] text-white/75">
              <li className="flex gap-3"><MapPin size={16} className="mt-1 text-[var(--pg-orange)] flex-shrink-0" /> 8700 Commerce Park Drive, Suite 238, Houston, Texas 77036</li>
              <li className="flex gap-3"><Phone size={16} className="text-[var(--pg-orange)]" /> <a href="tel:+12819019914" className="hover:text-white">281-901-9914</a></li>
              <li className="flex gap-3"><Mail size={16} className="text-[var(--pg-orange)]" /> <a href="mailto:info@pgpsusa.com" className="hover:text-white">info@pgpsusa.com</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
          <p className="text-white/55 text-[13px]">© {new Date().getFullYear()} Patrick Global Procurement Services. All rights reserved.</p>
          <p className="text-white/55 text-[13px]">Procurement &amp; Supply Chain Company · Houston, TX</p>
        </div>
      </div>
    </footer>
  );
}

/* ----------------------- PAGE --------------------------- */
export default function LandingPage() {
  const goContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  return (
    <div data-testid="landing-page">
      <NavBar onContactClick={goContact} />
      <main>
        <Hero onContactClick={goContact} />
        <TrustStrip />
        <Services />
        <ValueProp />
        <WhoWeServe />
        <Industries />
        <OperationsGallery />
        <About />
        <MedicalSubsidiary onContactClick={goContact} />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}
